import { useRef } from "react";
import { useFrame } from "@react-three/fiber";

/**
 * Runtime frame-rate guard. Renders nothing; it samples the render loop and, if
 * the average FPS stays below `minFps` for `sustainS` seconds, fires `onDemote`
 * exactly once. The parent (App) then drops to a lighter tier (e.g. high -> mid
 * -> low procedural) so a struggling device degrades gracefully mid-session
 * instead of stuttering for the whole scenario.
 *
 * Disable on the lowest tier (nothing left to demote to).
 */
export function FpsWatchdog({
  enabled,
  onDemote,
  minFps = 24,
  sustainS = 4,
  graceS = 2.5,
}: {
  enabled: boolean;
  onDemote: () => void;
  /** demote below this average frame rate */
  minFps?: number;
  /** how long FPS must stay low before demoting */
  sustainS?: number;
  /** ignore the first moments (asset upload / JIT warm-up spikes) */
  graceS?: number;
}) {
  const frames = useRef(0);
  const windowStart = useRef(performance.now());
  const lowFor = useRef(0);
  const startedAt = useRef(performance.now());
  const fired = useRef(false);

  useFrame(() => {
    if (!enabled || fired.current) return;

    frames.current += 1;
    const now = performance.now();
    const elapsed = (now - windowStart.current) / 1000;
    if (elapsed < 1) return; // sample once per ~second

    const fps = frames.current / elapsed;
    frames.current = 0;
    windowStart.current = now;

    // Skip the warm-up window (decode/upload spikes are not representative).
    if ((now - startedAt.current) / 1000 < graceS) return;

    if (fps < minFps) {
      lowFor.current += elapsed;
      if (lowFor.current >= sustainS) {
        fired.current = true;
        onDemote();
      }
    } else {
      lowFor.current = 0;
    }
  });

  return null;
}
